import {AfterViewInit, Component, ElementRef, Input, ViewChild} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';

@Component({
  selector: 'app-signature',
  imports: [
    DatePipe,
    NgClass
  ],
  standalone: true,
  templateUrl: './signature.component.html'
})
export class SignatureComponent implements AfterViewInit {
  @Input() customClass: string;
  @Input() city: string;

  @ViewChild('signatureCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  private ctx!: CanvasRenderingContext2D;
  private drawing = false;

  touched = false;
  today = new Date();

  ngAfterViewInit() {
    const canvas = this.canvasRef.nativeElement;
    this.ctx = canvas.getContext('2d')!;

    this.ctx.strokeStyle = 'black';
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';

    this.resizeCanvas();

    canvas.addEventListener('mousedown', this.startDraw);
    canvas.addEventListener('mousemove', this.draw);
    canvas.addEventListener('mouseup', this.stopDraw);
    canvas.addEventListener('mouseleave', this.stopDraw);

    canvas.addEventListener('touchstart', this.startDraw, { passive: false });
    canvas.addEventListener('touchmove', this.draw, { passive: false });
    canvas.addEventListener('touchend', this.stopDraw);

    window.addEventListener('resize', this.resizeCanvas.bind(this));
    window.addEventListener('orientationchange', this.resizeCanvas.bind(this));
  }

  private resizeCanvas() {
    const canvas = this.canvasRef.nativeElement;

    // save current drawing
    const data = canvas.toDataURL();

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.scale(dpr, dpr);

    // restore drawing
    if (data !== 'data:,') {
      this.loadFromDataURL(data);
    }
  }

  startDraw = (event: MouseEvent | TouchEvent) => {
    this.drawing = true;
    this.ctx.beginPath();
    const { x, y } = this.getCoords(event);
    this.ctx.moveTo(x, y);
  };

  draw = (event: MouseEvent | TouchEvent) => {
    if (!this.drawing) return;
    event.preventDefault();
    const { x, y } = this.getCoords(event);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.touched = true;
  };

  stopDraw = () => {
    this.drawing = false;
    this.ctx.closePath();
  };

  getCoords(event: MouseEvent | TouchEvent) {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX = 0, clientY = 0;

    if (event instanceof MouseEvent) {
      clientX = event.clientX;
      clientY = event.clientY;
    } else {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    }

    return {
      x: (clientX - rect.left),
      y: (clientY - rect.top)
    };
  }

  clearCanvas() {
    const canvas = this.canvasRef.nativeElement;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.touched = false;
  }

  getDataURL(): string {
    const canvas = this.canvasRef.nativeElement;
    return canvas.toDataURL();
  }

  // load a previously saved signature (dataURL) onto the canvas
  loadFromDataURL(dataURL: string) {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const img = new Image();
    img.onload = () => {
      this.ctx.clearRect(0, 0, rect.width, rect.height);
      this.ctx.drawImage(
        img,
        0,
        0,
        rect.width,
        rect.height
      );
      this.touched = true;
    };
    img.src = dataURL;
  }
}
